import z from 'zod';

/**
 * Represents any valid JSON primitive value.
 */
export type JSONPrimitive = string | number | boolean | null;

/**
 * Represents any valid JSON value (recursive).
 */
export type JSONValue = JSONPrimitive | JSONObject | JSONArray;

/**
 * A JSON object with string keys and JSON values.
 */
export interface JSONObject {
  [key: string]: JSONValue;
}

/**
 * A JSON array of JSON values.
 */
export type JSONArray = JSONValue[];

/**
 * JSONB type for use with Drizzle `$type<JSONB>()` annotations.
 * Supports both objects and arrays at the top level.
 * For entity-specific JSONB columns, prefer domain types (e.g. CustomRibbonFilters).
 */
export type JSONB = JSONObject | JSONArray;

/**
 * Zod schema for validating JSONB values (arrays or objects at the top level).
 * Nested values are validated recursively against valid JSON types.
 */
const jsonValueSchema: z.ZodType<JSONValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ]),
);

export const jsonbSchema: z.ZodType<JSONB> = z.union([
  z.array(jsonValueSchema),
  z.record(z.string(), jsonValueSchema),
]);

/**
 * Allowed value types for a single custom attribute field.
 *
 * - `string` — Text, Textarea, Date, Link, Select
 * - `number` — supported by external API
 * - `boolean` — supported by external API
 * - `string[]` — MultiSelect, UserMultiSelect, DepartmentMultiSelect, Tags
 * - `null` — unset/empty field
 */
export type CustomAttributeValue =
  | string
  | number
  | boolean
  | string[]
  | null;

/**
 * Custom attribute data stored on entities. A flat key-value map where keys
 * are custom field identifiers (e.g. `controlType__fieldName`) and values
 * are user-entered data constrained to {@link CustomAttributeValue}.
 */
export type CustomAttributeData = Record<string, CustomAttributeValue>;

const customAttributeValueSchema = z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);

/**
 * Zod schema for CustomAttributeData. Validates a string-keyed object with
 * values restricted to the supported custom field value types.
 *
 * Keys whose value is `undefined` are stripped before validation: an unset
 * custom-attribute field is semantically "not provided". Forms submit blank
 * fields as `undefined`, and unlike the legacy GraphQL path (where
 * `JSON.stringify` dropped them), the tRPC + superjson transport preserves
 * `undefined` over the wire — so they must be filtered here rather than
 * failing the value union (RSP-5940). Explicit `null` is preserved as a
 * deliberate "cleared" marker.
 */
export const customAttributeDataSchema: z.ZodType<
  CustomAttributeData,
  z.ZodTypeDef,
  unknown
> = z.preprocess((val) => {
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    // Narrowed to a non-array object above; each value is validated below.
    const record = val as Record<string, unknown>;

    return Object.fromEntries(
      Object.entries(record).filter(([, value]) => value !== undefined)
    );
  }

  return val;
}, z.record(z.string(), customAttributeValueSchema));

export const auditFieldSchema = z.object({
  createdByUser: z.string(),
  modifiedByUser: z.string(),
  createdAtTimestamp: z.date(), // todo should these be strings? node is a bit inconsistent with date handling
  modifiedAtTimestamp: z.date(),
});
